import { useOverview } from '../api'
import { useLang } from '../lang'
import { useFollow } from '../favourites'
import { Collage } from './Collage'
import { TodaySpark } from './TodaySpark'
import { AlmanacRow } from './AlmanacRow'
import { NotableBlock } from './NotableBlock'
import { LiveLists } from './LiveLists'
import { DetectionStats } from './DetectionStats'
import { StateLine } from '../design'
import type { NotableItem } from '../types'
import * as live from './live.css'

// LIVE — the listening instrument, present tense. One calm composition: the collage stage (what is
// being heard), then a single day strip (the day's shape + almanac + the running tally), then New &
// notable and Recently heard. No stacked sticky bands, no scroll-measuring machinery — the masthead
// is the only sticky thing now. The warm, narrated story of a COMPLETED day lives on the Journal.
export function LiveTab({
  onSelect,
  windowHours,
  onWindow,
  windows,
  mark,
}: {
  onSelect: (sci: string) => void
  windowHours: number
  onWindow: (hours: number) => void
  windows: [string, number][]
  mark: string | null
}) {
  const { data, isLoading, isError } = useOverview(windowHours)
  const { t } = useLang()
  const { enabled, following } = useFollow()

  // The waiting state RESERVES the collage's box rather than collapsing to a line of dots, so the
  // first paint doesn't lurch when the ~780px collage replaces a ~140px stub (a 0.93 layout shift).
  // The box is reserved in CSS via the collage's fixed 800×480 aspect.
  if (isLoading || !data) {
    return (
      <section className="ed-stage is-waiting">
        <StateLine className="ed-stage-waiting" busy={!isError}>
          {isError ? '—' : '…'}
        </StateLine>
      </section>
    )
  }

  // Your favourites: the signed-in reader's followed birds actually heard in this window — the
  // collage nodes are exactly that set. Computed client-side so the cacheable, cookie-free
  // /api/overview stays personalisation-free.
  const favourites: NotableItem[] = enabled
    ? data.collage.nodes.filter((n) => following(n.sci)).map((n) => ({ sci: n.sci, en: n.en, ga: n.ga }))
    : []

  const heardSomething = data.collage.nodes.length > 0

  return (
    <>
      <section className="ed-stage">
        <Collage data={data.collage} onSelect={onSelect} status={data.status} />
      </section>

      {/* The one day strip: the sparkline + window picker and the almanac are NOT about heard
          birds, so they show even in an empty window; the running tally joins them only when
          something has actually been heard. */}
      <div className={live.dayStrip}>
        <TodaySpark today={data.today} windows={windows} value={windowHours} onChange={onWindow} />
        <AlmanacRow today={data.today} status={data.status} />
        {heardSomething && (
          <DetectionStats
            detections={data.stats.detections}
            species={data.stats.species}
            durationSeconds={data.stats.duration_seconds}
          />
        )}
      </div>

      {heardSomething ? (
        <>
          <NotableBlock groups={data.notable} favourites={favourites} onSelect={onSelect} />
          <LiveLists recent={data.recent} onSelect={onSelect} />
        </>
      ) : (
        // A branded Cuckoo moment — the three honest empty states stay distinct in the text.
        <div className={live.empty}>
          {mark && <img className={live.emptyMark} src={mark} alt="" />}
          <StateLine className={live.emptyText}>
            {data.status === 'offline'
              ? t('Offline — the mic is quiet.', 'As líne — tá an micreafón ina thost.')
              : t('Listening — nothing heard yet.', 'Ag éisteacht — faic cloiste fós.')}
          </StateLine>
        </div>
      )}
    </>
  )
}
